import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TITLE = { recipients: "Bulk import recipients (Excel / CSV)", allocation: "Bulk allocate serials / asset tags (Excel / CSV)" };
const TONE = { OK: "bg-emerald-100 text-emerald-800", DUPLICATE: "bg-amber-100 text-amber-900", INVALID: "bg-rose-100 text-rose-800" };
const summary = (k, r) => (k === "recipients" ? [r.recipient_name, r.email, [r.city, r.pin].filter(Boolean).join(" "), r.qty > 1 ? `×${r.qty}` : ""] : [r.recipient, r.serial, r.asset_tag]).filter(Boolean).join(" · ");

/** Upload → map columns → validate (preview, no writes) → import valid rows only; skipped rows listed with reasons. */
export function BulkImportDialog({ qid, kind, onClose, onDone }) {
  const [sheet, setSheet] = useState(null);
  const [map, setMap] = useState({});
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const err = (e) => toast.error(e.response?.data?.detail || e.message);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { const { data } = await api.post(`/quote-operations/quotes/${qid}/import/sheet?kind=${kind}`, fd); setSheet(data); setMap(data.suggested); setRes(null); } catch (e) { err(e); }
    setBusy(false);
  };
  const body = () => ({ kind, rows: sheet.rows, mapping: Object.fromEntries(Object.entries(map).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)])) });
  const validate = async () => { setBusy(true); try { setRes((await api.post(`/quote-operations/quotes/${qid}/import/validate`, body())).data); } catch (e) { err(e); } setBusy(false); };
  const commit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/quote-operations/quotes/${qid}/import/commit`, body()); toast.success(`${data.imported} row(s) imported${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`); onDone?.(); onClose(); } catch (e) { err(e); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto" data-testid={`bulk-import-${kind}`}>
        <DialogHeader><DialogTitle>{TITLE[kind]}</DialogTitle></DialogHeader>
        <label className="block text-xs">1. Choose a .xlsx or .csv file (first row = column headings)
          <input type="file" accept=".xlsx,.xlsm,.csv,.txt" onChange={(e) => upload(e.target.files?.[0])} className="mt-1 block text-xs" data-testid="bulk-import-file" />
        </label>
        {sheet && (
          <div className="space-y-2" data-testid="bulk-import-mapping">
            <div className="text-xs font-semibold">2. Match your columns ({sheet.count} data rows)</div>
            <div className="grid gap-2 sm:grid-cols-3">
              {sheet.fields.map((f) => (
                <label key={f.key} className="text-[11px]">{f.label}{f.required && <span className="text-rose-600"> *</span>}
                  <select value={map[f.key] ?? ""} onChange={(e) => { setMap({ ...map, [f.key]: e.target.value }); setRes(null); }} className="mt-0.5 h-8 w-full rounded-md border border-slate-300 bg-white px-1 text-xs" data-testid={`bulk-map-${f.key}`}>
                    <option value="">— not in file —</option>
                    {sheet.headers.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                  </select>
                </label>
              ))}
            </div>
            <button onClick={validate} disabled={busy} className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50" data-testid="bulk-import-validate">3. Check rows</button>
          </div>
        )}
        {res && (
          <div className="space-y-2" data-testid="bulk-import-result">
            <div className="text-xs"><b className="text-emerald-700" data-testid="bulk-import-valid">{res.valid} ready to import</b> · <b className="text-rose-700" data-testid="bulk-import-skipped">{res.skipped} will be skipped</b></div>
            <div className="max-h-72 overflow-auto rounded border border-slate-200">
              <table className="w-full text-[11px]"><tbody>
                {res.rows.map((r) => (
                  <tr key={r.row_no} className="border-b border-slate-100" data-testid={`bulk-row-${r.row_no}`}>
                    <td className="px-2 py-1 text-slate-500">Row {r.row_no}</td>
                    <td className="px-2 py-1"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${TONE[r.status]}`}>{r.status === "OK" ? "Import" : r.status === "DUPLICATE" ? "Duplicate" : "Skip"}</span></td>
                    <td className="px-2 py-1">{summary(kind, r.record)}</td>
                    <td className="px-2 py-1 text-rose-700">{r.errors.join("; ")}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
            <button onClick={commit} disabled={busy || !res.valid} className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="bulk-import-commit">4. Import {res.valid} valid row(s)</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
